<template>
  <b-modal
    id="get-form-qr-modal"
    :visible="value"
    @show="onShow"
    @hidden="onHidden"
    @change="$emit('input', $event)"
    :title="modalTitle"
    body-class="bg-white p-0 get-form-qr-body"
    size="lg"
    scrollable
    hide-header-close
  >
    <!-- Patient context + search (sticky at top) -->
    <div class="sticky-header">
      <div class="patient-context">
        <i class="fa fa-user-circle"></i>
        <div class="patient-context-text">
          <div class="patient-context-label">For Patient</div>
          <div class="patient-context-name">{{ patientDisplayName }}</div>
        </div>
        <div v-if="!selectedItem" class="search-wrap">
          <i class="fa fa-search search-icon"></i>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search forms by name..."
            class="form-control form-control-sm search-input"
            ref="searchInput"
          />
          <i v-if="searchQuery" class="fa fa-times-circle clear-search" @click="searchQuery = ''" title="Clear search"></i>
        </div>
      </div>
    </div>

    <!-- LIST VIEW -->
    <template v-if="!selectedItem">

      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <i class="fa fa-spinner fa-spin"></i>
        <p>Loading forms...</p>
      </div>

      <!-- Empty: no forms at all -->
      <div v-else-if="combinedItems.length === 0" class="state-block">
        <i class="fa fa-file-text-o"></i>
        <p>No forms available. Create a form on the Teamio Forms page first.</p>
      </div>

      <!-- Search active: flat list -->
      <template v-else-if="searchActive">
        <div v-if="searchResults.length === 0" class="state-block">
          <i class="fa fa-search"></i>
          <p>No forms match "{{ searchQuery }}"</p>
        </div>
        <div v-else class="form-list">
          <div
            v-for="item in searchResults"
            :key="item._id"
            class="form-row"
            @click="selectItem(item)"
          >
            <i :class="item._isPacket ? 'fa fa-files-o' : 'fa fa-file-text-o'" class="form-row-icon"></i>
            <div class="form-row-name">
              {{ item.Name }}
              <span v-if="item._isPacket" class="badge-pill packet-badge">PACKET</span>
              <span
                v-for="lang in getFormTranslationLanguages(item)"
                :key="lang"
                class="badge-pill lang-badge"
                :title="`${lang.toUpperCase()} translation enabled`"
              >{{ lang.toUpperCase() }}</span>
            </div>
            <i class="fa fa-chevron-right form-row-chevron"></i>
          </div>
        </div>
      </template>

      <!-- Sectioned view -->
      <template v-else>
        <!-- Procedures on the appointment — consent scoped to the actual work.
             Each row's QR prefills the form's "Teeth #s" with ONLY that
             procedure's teeth, instead of every tooth on the appointment. -->
        <template v-if="activeAppointment">
          <!-- Card, not another section-header: this must read as a distinct panel
               about THIS patient's visit, not as a third group in the form list. -->
          <div class="proc-card" :class="{ 'is-completed': activeAppointment.isRecentlyCompleted }">
            <div class="proc-card-header">
              <i class="fa fa-stethoscope"></i>
              <span class="proc-card-title">{{ appointmentHeadline }}</span>
              <span class="proc-card-count">· {{ procedureCount }} procedure{{ procedureCount === 1 ? '' : 's' }}</span>
              <span class="apt-date">{{ formatAptDate(activeAppointment) }}</span>
            </div>

            <!-- Appointment switcher: today's visit, the next scheduled one, and a
                 recently-completed one can all be in scope — let staff pick. -->
            <div v-if="appointments.length > 1" class="apt-switcher">
              <button
                v-for="(apt, idx) in appointments"
                :key="apt.aptNum"
                class="apt-chip"
                :class="{ active: idx === selectedAptIndex, completed: apt.isRecentlyCompleted }"
                @click="selectedAptIndex = idx"
              >
                {{ formatAptDate(apt) }}
                <i v-if="apt.isRecentlyCompleted" class="fa fa-check chip-done" title="Completed"></i>
              </button>
            </div>

            <div class="proc-table">
            <div class="proc-row proc-head">
              <div class="proc-col-code">Procedure</div>
              <div class="proc-col-teeth">Teeth</div>
              <div class="proc-col-form">Consent Form</div>
              <div class="proc-col-chevron"></div>
            </div>

            <div
              v-for="group in activeAppointment.groups"
              :key="'g-' + group.formIDForWeb"
              class="proc-row proc-mapped"
              @click="selectProcedureGroup(group)"
            >
              <div class="proc-col-code">
                <span v-for="code in group.codes" :key="code.procCode" class="proc-code-chip">
                  {{ code.procCode }}<span v-if="code.count > 1" class="proc-count">×{{ code.count }}</span>
                </span>
                <div class="proc-descript">{{ groupDescription(group) }}</div>
              </div>
              <div class="proc-col-teeth">
                <span v-if="group.teeth" class="teeth-badge">{{ group.teeth }}</span>
                <span v-else class="teeth-none" title="This procedure is not tooth-specific">—</span>
              </div>
              <div class="proc-col-form">{{ group.formName }}</div>
              <div class="proc-col-chevron"><i class="fa fa-chevron-right"></i></div>
            </div>

            <!-- Unmapped: shown, not hidden, so the gap is visible and fixable. -->
            <div
              v-for="item in activeAppointment.unmapped"
              :key="'u-' + item.procCode"
              class="proc-row proc-unmapped"
            >
              <div class="proc-col-code">
                <span class="proc-code-chip">
                  {{ item.procCode }}<span v-if="item.count > 1" class="proc-count">×{{ item.count }}</span>
                </span>
                <div class="proc-descript">{{ item.descript }}</div>
              </div>
              <div class="proc-col-teeth">
                <span v-if="item.teeth" class="teeth-badge muted">{{ item.teeth }}</span>
                <span v-else class="teeth-none">—</span>
              </div>
              <div class="proc-col-form no-form">No consent form mapped</div>
              <div class="proc-col-chevron"></div>
            </div>
            </div>

            <div v-if="!mappingsConfigured" class="proc-hint">
              <i class="fa fa-info-circle"></i>
              Map procedure codes to consent forms in <strong>Forms &rarr; 🔗</strong>
              to pick the right consent straight from this list.
            </div>
          </div>

          <!-- Explicit hand-off: everything below is the ordinary form library. -->
          <div class="proc-divider"><span>or pick any form below</span></div>
        </template>

        <!-- Consent Forms -->
        <template v-if="consentItems.length > 0">
          <div class="section-header consent-header">
            <i class="fa fa-shield"></i> Consent Forms
          </div>
          <div class="form-list">
            <div
              v-for="item in consentItems"
              :key="item._id"
              class="form-row"
              @click="selectItem(item)"
            >
              <i :class="item._isPacket ? 'fa fa-files-o' : 'fa fa-file-text-o'" class="form-row-icon"></i>
              <div class="form-row-name">
                {{ item.Name }}
                <span v-if="item._isPacket" class="badge-pill packet-badge">PACKET</span>
                <span
                  v-for="lang in getFormTranslationLanguages(item)"
                  :key="lang"
                  class="badge-pill lang-badge"
                >{{ lang.toUpperCase() }}</span>
              </div>
              <i class="fa fa-chevron-right form-row-chevron"></i>
            </div>
          </div>
        </template>

        <!-- Pinned -->
        <template v-if="pinnedItems.length > 0">
          <div class="section-header pinned-header">
            <i class="fa fa-thumb-tack"></i> Pinned
          </div>
          <div class="form-list">
            <div
              v-for="item in pinnedItems"
              :key="item._id"
              class="form-row"
              @click="selectItem(item)"
            >
              <i :class="item._isPacket ? 'fa fa-files-o' : 'fa fa-file-text-o'" class="form-row-icon"></i>
              <div class="form-row-name">
                {{ item.Name }}
                <span v-if="item._isPacket" class="badge-pill packet-badge">PACKET</span>
                <span
                  v-for="lang in getFormTranslationLanguages(item)"
                  :key="lang"
                  class="badge-pill lang-badge"
                >{{ lang.toUpperCase() }}</span>
              </div>
              <i class="fa fa-chevron-right form-row-chevron"></i>
            </div>
          </div>
        </template>

        <!-- All Forms -->
        <template v-if="allItems.length > 0">
          <div class="section-header all-header">
            All Forms
            <span class="section-count">{{ allItemsRangeLabel }}</span>
          </div>
          <div class="form-list">
            <div
              v-for="item in paginatedAllItems"
              :key="item._id"
              class="form-row"
              @click="selectItem(item)"
            >
              <i :class="item._isPacket ? 'fa fa-files-o' : 'fa fa-file-text-o'" class="form-row-icon"></i>
              <div class="form-row-name">
                {{ item.Name }}
                <span v-if="item._isPacket" class="badge-pill packet-badge">PACKET</span>
                <span
                  v-for="lang in getFormTranslationLanguages(item)"
                  :key="lang"
                  class="badge-pill lang-badge"
                >{{ lang.toUpperCase() }}</span>
              </div>
              <i class="fa fa-chevron-right form-row-chevron"></i>
            </div>
          </div>
          <div v-if="allItemsTotalPages > 1" class="pagination-bar">
            <button class="pagination-btn" :disabled="allItemsPage === 1" @click="allItemsPage = 1">
              <i class="fa fa-angle-double-left"></i>
            </button>
            <button class="pagination-btn" :disabled="allItemsPage === 1" @click="allItemsPage--">
              <i class="fa fa-angle-left"></i>
            </button>
            <span class="pagination-page">Page {{ allItemsPage }} of {{ allItemsTotalPages }}</span>
            <button class="pagination-btn" :disabled="allItemsPage === allItemsTotalPages" @click="allItemsPage++">
              <i class="fa fa-angle-right"></i>
            </button>
            <button class="pagination-btn" :disabled="allItemsPage === allItemsTotalPages" @click="allItemsPage = allItemsTotalPages">
              <i class="fa fa-angle-double-right"></i>
            </button>
          </div>
        </template>
      </template>
    </template>

    <!-- QR VIEW -->
    <template v-else>
      <div class="qr-view">
        <div class="qr-header">
          <button class="back-btn" @click="backToList" :disabled="qrLoading">
            <i class="fa fa-arrow-left"></i> Back to forms
          </button>
        </div>

        <div class="qr-form-name">
          <i :class="selectedItem._isPacket ? 'fa fa-files-o' : 'fa fa-file-text-o'"></i>
          {{ selectedItem.Name }}
          <span v-if="selectedItem._isPacket" class="badge-pill packet-badge">PACKET</span>
        </div>

        <!-- Confirms what this QR will prefill, so staff can catch a wrong pick
             before handing the tablet to the patient. -->
        <div v-if="procScope" class="qr-scope">
          <i class="fa fa-check-circle"></i>
          Prefills teeth
          <strong>{{ procScope.teeth || 'none' }}</strong>
          <span class="qr-scope-codes">({{ procScope.codeLabel }})</span>
        </div>

        <div v-if="qrLoading" class="qr-loading">
          <i class="fa fa-spinner fa-spin"></i>
          <p>Generating QR code...</p>
        </div>

        <div v-else-if="qrError" class="qr-error">
          <i class="fa fa-exclamation-triangle"></i>
          <p>{{ qrError }}</p>
          <button class="btn btn-sm btn-outline-primary" @click="generateUrlAndQR">Try again</button>
        </div>

        <template v-else>
          <div class="qr-canvas-wrap">
            <canvas ref="qrCanvas"></canvas>
          </div>

          <div class="qr-instructions">
            Scan with the patient's tablet or phone, or copy the link below.
          </div>

          <div class="link-display-wrapper">
            <input
              type="text"
              class="link-input"
              :value="shortUrl"
              readonly
              ref="linkInput"
              @click="$refs.linkInput.select()"
            />
            <button class="copy-link-btn" @click="copyLink" title="Copy to clipboard">
              <i class="fa fa-copy"></i>
            </button>
          </div>
        </template>
      </div>
    </template>

    <!-- Footer -->
    <div slot="modal-footer" class="w-100">
      <b-button
        variant="outline-secondary"
        size="sm"
        class="float-right"
        @click="close"
      >Close</b-button>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import QRCode from 'qrcode';

export default {
  name: 'GetFormQRModal',

  props: {
    value: { type: Boolean, default: false },
    patientData: { type: Object, default: null },
    patientNum: { type: [String, Number], default: null },
    siteId: { type: String, default: null },
    // Site timezone (GENERAL_TIMEZONE). Passed down because this component does
    // not include the global mixin that exposes allSettings.
    timezone: { type: String, default: null }
  },

  data() {
    return {
      loading: false,
      forms: [],
      packets: [],
      searchQuery: '',
      selectedItem: null,
      shortUrl: '',
      qrLoading: false,
      qrError: '',
      allItemsPage: 1,
      allItemsPerPage: 10,
      // Procedure-scoped consent (see the "Procedures on this appointment" section)
      appointments: [],
      mappingsConfigured: false,
      selectedAptIndex: 0,
      // { aptNum, codes: [], teeth, codeLabel } for the currently selected
      // procedure group — appended to the short URL as ?aptNum=&codes=.
      procScope: null
    };
  },

  computed: {
    modalTitle() {
      return this.selectedItem ? 'Form Link & QR Code' : 'Get Form QR Code';
    },

    patientDisplayName() {
      if (!this.patientData) return '';
      const name = this.patientData.PatientName || '';
      const patNum = this.patientNum != null ? this.patientNum : '';
      return patNum ? `${name} (ID: ${patNum})` : name;
    },

    combinedItems() {
      const formItems = (this.forms || []).map(f => ({
        ...f,
        _isPacket: false,
        _id: `form-${f.FormIDForWeb}`
      }));
      const packetItems = (this.packets || []).map(p => ({
        ...p,
        _isPacket: true,
        _id: `packet-${p.PacketID}`
      }));
      return [...formItems, ...packetItems];
    },

    consentItems() {
      return this.sortByName(
        this.combinedItems.filter(item => this.isConsent(item))
      );
    },

    pinnedItems() {
      return this.sortByName(
        this.combinedItems.filter(item => !this.isConsent(item) && this.isPinned(item))
      );
    },

    allItems() {
      return this.sortByName(
        this.combinedItems.filter(item => !this.isConsent(item) && !this.isPinned(item))
      );
    },

    allItemsTotalPages() {
      return Math.max(1, Math.ceil(this.allItems.length / this.allItemsPerPage));
    },

    paginatedAllItems() {
      const start = (this.allItemsPage - 1) * this.allItemsPerPage;
      return this.allItems.slice(start, start + this.allItemsPerPage);
    },

    allItemsRangeLabel() {
      if (this.allItems.length === 0) return '';
      const start = (this.allItemsPage - 1) * this.allItemsPerPage + 1;
      const end = Math.min(start + this.allItemsPerPage - 1, this.allItems.length);
      return `${start}-${end} of ${this.allItems.length}`;
    },

    searchActive() {
      return this.searchQuery.trim().length > 0;
    },

    searchResults() {
      const q = this.searchQuery.trim().toLowerCase();
      return this.sortByName(
        this.combinedItems.filter(item => (item.Name || '').toLowerCase().includes(q))
      );
    },

    /**
     * The appointment whose procedures are on screen. Null hides the whole
     * section — no appointment in scope, or one with nothing to consent for.
     */
    activeAppointment() {
      const apt = this.appointments[this.selectedAptIndex] || null;
      if (!apt) return null;
      const hasRows = (apt.groups && apt.groups.length) || (apt.unmapped && apt.unmapped.length);
      return hasRows ? apt : null;
    },

    /**
     * Answers "why am I looking at this?" in three words. `isRecentlyCompleted`
     * comes from the backend (derived in SQL from the same CURDATE() that selected
     * the row) rather than being recomputed here against a timezone.
     */
    appointmentHeadline() {
      const apt = this.activeAppointment;
      if (!apt) return '';
      if (apt.isRecentlyCompleted) return 'Recently completed appointment';
      return this.isToday(apt) ? 'Appointment today' : 'Upcoming appointment';
    },

    /**
     * Distinct procedures on the appointment. Counted by ProcNum, not by summing
     * group counts — a code mapped to two consent forms appears in two groups and
     * would otherwise be double-counted.
     */
    procedureCount() {
      const apt = this.activeAppointment;
      if (!apt) return 0;
      const procNums = [
        ...(apt.groups || []).flatMap(g => g.procNums || []),
        ...(apt.unmapped || []).flatMap(u => u.procNums || []),
      ];
      return new Set(procNums).size;
    }
  },

  methods: {
    ...mapActions('forms', ['getForms', 'getPackets', 'getFormUrl', 'getPacketUrl', 'getAppointmentConsentForms']),

    /**
     * "Today, 2:00 PM" / "Fri Aug 14, 9:00 AM" — appointment chip + header label.
     *
     * OD stores AptDateTime as the practice's local wall-clock time, so it is
     * PARSED as the site timezone, never converted into it — same handling as the
     * `dateformatwithtime` filter the Scheduled Appointments card uses. Converting
     * would shift every appointment by the UTC offset.
     */
    formatAptDate(apt) {
      const d = this.aptMoment(apt);
      if (!d) return '';
      const now = this.nowMoment();
      if (d.isSame(now, 'day')) return `Today, ${d.format('h:mm A')}`;
      if (d.isSame(now.clone().subtract(1, 'day'), 'day')) return `Yesterday, ${d.format('h:mm A')}`;
      return d.format('ddd MMM D, h:mm A');
    },

    /** Parsed appointment time, or null. Shared by formatAptDate/isToday. */
    aptMoment(apt) {
      if (!apt || !apt.aptDateTime) return null;
      const d = this.timezone
        ? this.$moment.tz(apt.aptDateTime, this.timezone)
        : this.$moment(apt.aptDateTime);
      return d && d.isValid() ? d : null;
    },

    nowMoment() {
      return this.timezone ? this.$moment.tz(this.timezone) : this.$moment();
    },

    isToday(apt) {
      const d = this.aptMoment(apt);
      return !!d && d.isSame(this.nowMoment(), 'day');
    },

    /** Joined descriptions for a group's codes, deduped. */
    groupDescription(group) {
      const seen = [];
      (group.codes || []).forEach((c) => {
        const d = (c.descript || '').trim();
        if (d && !seen.includes(d)) seen.push(d);
      });
      return seen.join(' · ');
    },

    /**
     * Open the QR for a procedure group, carrying its scope so the form's
     * "Teeth #s" field prefills with only this consent's teeth.
     */
    selectProcedureGroup(group) {
      const apt = this.activeAppointment;
      if (!apt || !group.formIDForWeb) return;

      this.procScope = {
        aptNum: apt.aptNum,
        codes: group.codeNums || [],
        teeth: group.teeth,
        codeLabel: (group.codes || [])
          .map(c => c.count > 1 ? `${c.procCode} ×${c.count}` : c.procCode)
          .join(', ')
      };

      // Shape a list-item so the QR view header renders identically to a
      // form picked from the normal list.
      this.selectItem({
        Name: group.formName,
        FormIDForWeb: group.formIDForWeb,
        _isPacket: false,
        _id: `proc-${apt.aptNum}-${group.formIDForWeb}`
      }, true);
    },

    isConsent(item) {
      return (item.Name || '').toLowerCase().includes('consent');
    },

    isPinned(item) {
      return !!item.IsPinned;
    },

    sortByName(arr) {
      return [...arr].sort((a, b) =>
        (a.Name || '').toLowerCase().localeCompare((b.Name || '').toLowerCase())
      );
    },

    getFormTranslationLanguages(item) {
      if (!item || item._isPacket) return [];
      if (item.SupportedLanguages != null) {
        try {
          const langs = typeof item.SupportedLanguages === 'string'
            ? JSON.parse(item.SupportedLanguages)
            : item.SupportedLanguages;
          if (Array.isArray(langs)) return langs.filter(l => l !== 'en');
        } catch (_) { /* fall through */ }
      }
      if (item.SpanishEnabled === true || item.SpanishEnabled === 1 || item.SpanishEnabled === 'true') {
        return ['es'];
      }
      return [];
    },

    async onShow() {
      this.resetState();
      await this.loadData();
      this.$nextTick(() => {
        if (this.$refs.searchInput) this.$refs.searchInput.focus();
      });
    },

    onHidden() {
      this.resetState();
    },

    resetState() {
      this.searchQuery = '';
      this.selectedItem = null;
      this.shortUrl = '';
      this.qrLoading = false;
      this.qrError = '';
      this.allItemsPage = 1;
      this.appointments = [];
      this.mappingsConfigured = false;
      this.selectedAptIndex = 0;
      this.procScope = null;
    },

    async loadData() {
      if (!this.siteId) return;
      this.loading = true;
      try {
        // The procedure lookup rides along but must never block or break the
        // form list — its action swallows errors and resolves to null.
        const [formsRes, packetsRes, procRes] = await Promise.all([
          this.getForms({ siteID: this.siteId, includeConfigData: false }),
          this.getPackets({ siteID: this.siteId }),
          this.patientNum
            ? this.getAppointmentConsentForms({ siteID: this.siteId, patNum: this.patientNum })
            : Promise.resolve(null)
        ]);

        this.appointments = (procRes && Array.isArray(procRes.appointments)) ? procRes.appointments : [];
        this.mappingsConfigured = !!(procRes && procRes.mappingsConfigured);
        this.selectedAptIndex = 0;
        // getForms returns { forms, categories } — unwrap. Tolerate
        // a bare array too in case the backend shape ever changes back.
        if (Array.isArray(formsRes)) {
          this.forms = formsRes;
        } else if (formsRes && Array.isArray(formsRes.forms)) {
          this.forms = formsRes.forms;
        } else {
          this.forms = [];
        }
        this.packets = Array.isArray(packetsRes) ? packetsRes : [];
      } catch (err) {
        console.error('Failed to load forms/packets', err);
        this.$toast && this.$toast.error('Failed to load forms');
      } finally {
        this.loading = false;
      }
    },

    // keepScope is set only by selectProcedureGroup; picking a form from the
    // plain list must clear any scope left over from a previous selection.
    selectItem(item, keepScope = false) {
      if (!keepScope) this.procScope = null;
      this.selectedItem = item;
      this.shortUrl = '';
      this.qrError = '';
      this.$nextTick(() => this.generateUrlAndQR());
    },

    backToList() {
      this.selectedItem = null;
      this.shortUrl = '';
      this.qrError = '';
      this.procScope = null;
    },

    async generateUrlAndQR() {
      if (!this.selectedItem) return;
      const patNum = this.patientNum != null ? this.patientNum : null;
      this.qrLoading = true;
      this.qrError = '';
      this.shortUrl = '';
      try {
        let res;
        if (this.selectedItem._isPacket) {
          res = await this.getPacketUrl({
            siteID: this.siteId,
            packetID: this.selectedItem.PacketID,
            patNum
          });
        } else {
          res = await this.getFormUrl({
            siteID: this.siteId,
            formIDForWeb: this.selectedItem.FormIDForWeb,
            patNum
          });
        }
        this.shortUrl = this.applyProcedureScope(res && res.data ? res.data : '');
        this.qrLoading = false;
        await this.$nextTick();
        this.renderQR();
      } catch (err) {
        console.error('Failed to generate form URL', err);
        this.qrLoading = false;
        this.qrError = (err && err.message) || 'Failed to generate link.';
      }
    },

    /**
     * Append the procedure scope to a short URL. The backend intersects `codes`
     * with the codes actually mapped to the form, so this can only narrow the
     * teeth prefill, never widen it. Returns the URL untouched when unscoped —
     * which is every form picked from the normal list.
     */
    applyProcedureScope(url) {
      if (!url || !this.procScope || !this.procScope.codes.length) return url;
      const sep = url.indexOf('?') === -1 ? '?' : '&';
      return `${url}${sep}aptNum=${encodeURIComponent(this.procScope.aptNum)}`
        + `&codes=${encodeURIComponent(this.procScope.codes.join(','))}`;
    },

    renderQR() {
      const canvas = this.$refs.qrCanvas;
      if (!canvas || !this.shortUrl) return;
      QRCode.toCanvas(canvas, this.shortUrl, { width: 240 }, (error) => {
        if (error) {
          console.error('QR render error', error);
          this.qrError = 'Failed to render QR code.';
        }
      });
    },

    async copyLink() {
      if (!this.shortUrl) return;
      try {
        await navigator.clipboard.writeText(this.shortUrl);
        this.$toast && this.$toast.success('Link copied to clipboard!');
      } catch (err) {
        console.error('Clipboard copy failed', err);
        this.$toast && this.$toast.error('Failed to copy link');
      }
    },

    close() {
      this.$emit('input', false);
    }
  }
};
</script>

<style lang="scss" scoped>
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #f0f7f4;
  border-bottom: 1px solid #e0eae5;
}

.patient-context {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  padding: 12px 20px;

  > i.fa-user-circle {
    font-size: 28px;
    color: #28a745;
    flex-shrink: 0;
  }
}

.patient-context-text {
  flex-shrink: 0;
}

.patient-context-label {
  font-size: 11px;
  text-transform: uppercase;
  color: #6c757d;
  letter-spacing: 0.5px;
}

.patient-context-name {
  font-size: 15px;
  font-weight: 600;
  color: #212529;
  white-space: nowrap;
}

.search-wrap {
  position: relative;
  flex: 1;
  margin-left: auto;
  min-width: 180px;
  max-width: 320px;

  .search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #adb5bd;
    pointer-events: none;
  }

  .search-input {
    padding-left: 30px;
    padding-right: 30px;
    background: #fff;
  }

  .clear-search {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #adb5bd;
    cursor: pointer;

    &:hover { color: #495057; }
  }
}

.state-block {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;

  i {
    font-size: 32px;
    margin-bottom: 12px;
    display: block;
  }

  p { margin: 0; font-size: 14px; }
}

.section-header {
  display: flex;
  align-items: baseline;
  padding: 10px 20px 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #6c757d;
  background: #fafbfc;

  i { margin-right: 6px; }

  &.consent-header {
    color: #b8860b;
    i { color: #b8860b; }
  }
  &.pinned-header {
    color: #28a745;
    i { color: #28a745; }
  }
}

/* ─── Appointment card ───────────────────────────────────────────────
   Deliberately a CARD, not another .section-header: this block answers
   "does this patient have a visit, and which forms does it need?" and must
   register before the eye reaches the flat form list underneath. Inset
   margins + border keep it visually detached from the flush-edge rows. */
.proc-card {
  margin: 12px 16px 0;
  border: 1px solid #b6d4fe;
  border-radius: 8px;
  background: #f4f9ff;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(13, 110, 253, 0.08);

  /* A completed visit is a catch-up, not the live one — warm it to amber so the
     two are never confused at a glance. */
  &.is-completed {
    border-color: #ffe08a;
    background: #fffbf0;
    box-shadow: 0 1px 3px rgba(224, 168, 0, 0.1);

    .proc-card-header {
      background: #fff4d6;
      color: #8a6100;
      border-bottom-color: #ffe08a;
      i { color: #b8860b; }
    }
    .apt-chip.active { background: #b8860b; border-color: #b8860b; }
  }
}

.proc-card-header {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px;
  padding: 9px 14px;
  background: #e7f1ff;
  border-bottom: 1px solid #b6d4fe;
  color: #0a58ca;

  i { margin-right: 6px; color: #0d6efd; }
}

.proc-card-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.proc-card-count {
  font-size: 12px;
  font-weight: 600;
  opacity: 0.75;
}

.apt-date {
  margin-left: auto;
  font-weight: 600;
  font-size: 12.5px;
  color: #495057;
}

/* "or pick any form below" — an explicit hand-off from the appointment card to
   the ordinary form library, so the list below doesn't read as more of the card. */
.proc-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 14px 16px 2px;
  color: #adb5bd;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e9ecef;
  }
}

.apt-switcher {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.6);
  border-bottom: 1px solid #dbeafe;
}

.apt-chip {
  border: 1px solid #ced4da;
  background: #fff;
  border-radius: 12px;
  padding: 3px 12px;
  font-size: 12px;
  color: #495057;
  cursor: pointer;
  transition: all 0.12s;

  &:hover { border-color: #0d6efd; }
  &.active {
    background: #0d6efd;
    border-color: #0d6efd;
    color: #fff;
  }

  .chip-done {
    margin-left: 5px;
    font-size: 10px;
    color: #2b8a3e;
  }
  &.active .chip-done { color: #fff; }
}

.proc-table {
  background: #fff;
}

.proc-row {
  display: flex;
  align-items: flex-start;
  padding: 10px 20px;
  border-top: 1px solid #f1f3f5;
  font-size: 13px;

  &:first-child { border-top: none; }

  &.proc-mapped {
    cursor: pointer;
    transition: background 0.12s;
    &:hover { background: #f0f6ff; }
  }

  &.proc-unmapped {
    background: #fcfcfd;
    color: #868e96;
  }

  &.proc-head {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #adb5bd;
    background: #fff;
    padding-top: 6px;
    padding-bottom: 6px;
  }
}

.proc-col-code { flex: 1 1 42%; min-width: 0; padding-right: 10px; }
.proc-col-teeth { flex: 0 0 84px; }
.proc-col-form { flex: 1 1 38%; min-width: 0; padding-right: 10px; word-break: break-word; }
.proc-col-chevron {
  flex: 0 0 16px;
  text-align: right;
  color: #ced4da;
  font-size: 12px;
  padding-top: 2px;
}

.proc-code-chip {
  display: inline-block;
  background: #eef2f7;
  border-radius: 4px;
  padding: 1px 6px;
  margin: 0 4px 2px 0;
  font-family: monospace;
  font-size: 12px;
  font-weight: 600;
  color: #343a40;
}

.proc-count {
  margin-left: 3px;
  font-weight: 400;
  color: #6c757d;
}

.proc-descript {
  font-size: 11px;
  color: #868e96;
  line-height: 1.35;
  margin-top: 2px;
}

.teeth-badge {
  display: inline-block;
  background: #e7f5ff;
  color: #1971c2;
  border-radius: 4px;
  padding: 1px 7px;
  font-weight: 700;
  font-size: 12px;

  &.muted {
    background: #f1f3f5;
    color: #868e96;
  }
}

.teeth-none { color: #ced4da; }

.proc-col-form.no-form {
  font-style: italic;
  color: #adb5bd;
}

.proc-hint {
  padding: 8px 14px;
  font-size: 12px;
  color: #6c757d;
  background: rgba(255, 255, 255, 0.7);
  border-top: 1px solid #e9ecef;

  i { color: #0d6efd; margin-right: 5px; }
}

.qr-scope {
  margin: -4px 0 12px;
  font-size: 12.5px;
  color: #2b8a3e;

  i { margin-right: 5px; }
  strong { color: #1971c2; }

  .qr-scope-codes {
    color: #868e96;
    margin-left: 4px;
  }
}

.section-count {
  margin-left: auto;
  font-size: 10px;
  font-weight: 500;
  color: #adb5bd;
  letter-spacing: 0.3px;
  text-transform: none;
}

.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 20px;
  background: #fafbfc;
  border-top: 1px solid #f1f3f5;

  .pagination-btn {
    background: #fff;
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 4px 10px;
    color: #495057;
    font-size: 12px;
    cursor: pointer;
    transition: background 0.12s;

    &:hover:not(:disabled) {
      background: #e9ecef;
    }
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .pagination-page {
    font-size: 12px;
    color: #495057;
    margin: 0 8px;
  }
}

.form-list {
  border-bottom: 1px solid #eee;
}

.form-row {
  display: flex;
  align-items: center;
  padding: 10px 20px;
  cursor: pointer;
  border-top: 1px solid #f1f3f5;
  transition: background 0.12s;

  &:first-child { border-top: none; }
  &:hover { background: #f8f9fa; }

  .form-row-icon {
    color: #6c757d;
    width: 18px;
    margin-right: 12px;
    flex-shrink: 0;
  }

  .form-row-name {
    flex: 1;
    font-size: 14px;
    color: #212529;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
  }

  .form-row-chevron {
    color: #adb5bd;
    margin-left: 8px;
    flex-shrink: 0;
  }
}

.badge-pill {
  display: inline-block;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  border-radius: 10px;
  letter-spacing: 0.3px;

  &.packet-badge {
    background: #e7f1ff;
    color: #0d6efd;
  }
  &.lang-badge {
    background: #fff3cd;
    color: #856404;
  }
}

.qr-view {
  padding: 16px 20px 20px;
}

.qr-header { margin-bottom: 12px; }

.back-btn {
  background: none;
  border: none;
  color: #007bff;
  font-size: 13px;
  padding: 4px 0;
  cursor: pointer;

  &:hover { text-decoration: underline; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  i { margin-right: 4px; }
}

.qr-form-name {
  font-size: 16px;
  font-weight: 600;
  color: #212529;
  margin-bottom: 16px;
  text-align: center;

  i { margin-right: 6px; color: #6c757d; }
}

.qr-loading,
.qr-error {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;

  i {
    font-size: 32px;
    margin-bottom: 12px;
    display: block;
  }
  p { margin: 0 0 12px; font-size: 14px; }
}

.qr-error i { color: #dc3545; }

.qr-canvas-wrap {
  display: flex;
  justify-content: center;
  padding: 16px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  margin-bottom: 12px;
}

.qr-instructions {
  text-align: center;
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 12px;
}

.link-display-wrapper {
  display: flex;
  align-items: stretch;
  border: 1px solid #ced4da;
  border-radius: 4px;
  overflow: hidden;

  .link-input {
    flex: 1;
    border: none;
    padding: 8px 12px;
    font-size: 13px;
    outline: none;
    background: #f8f9fa;
  }

  .copy-link-btn {
    border: none;
    background: #28a745;
    color: #fff;
    padding: 0 14px;
    cursor: pointer;
    transition: background 0.12s;

    &:hover { background: #218838; }
  }
}
</style>
